<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getTranslation } from '@/composables/data-display-utils';
import type { Locales } from '@/services/types/api.types';
import type { BreadcrumbElement, BreadcrumbDropdownOption } from '@/helpers/breadcrumbs.helper';

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  elements: BreadcrumbElement[];
  languageEnabled: Locales[];
}>();

// COMPUTED
const getRouteTranslation = computed(() => {
  return (element: BreadcrumbElement) => {
    const routeName = typeof element.to === 'object' ? (element.to as { name?: string })?.name : undefined;
    if (routeName) {
      if (routeName.includes('consultation')) return t('breadcrumbs.consultation');
      if (routeName.includes('section')) return t('breadcrumbs.sections');
      if (routeName.includes('form')) return t('breadcrumbs.forms');
      if (routeName.includes('project')) return t('breadcrumbs.project');
      if (routeName.includes('module')) return t('breadcrumbs.modules');
    }
    // A crumb its route does not name is named by its label: a page's blocks crumb, by the page's word for them.
    if (typeof element.label === 'string') return element.label;
    return getTranslation(element, 'label', props.languageEnabled);
  };
});

const resolveOptionLabel = (option: BreadcrumbDropdownOption) => {
  return typeof option.label === 'string' ? option.label : getTranslation(option, 'label', props.languageEnabled);
};
</script>

<template>
  <div class="breadcrumbs">
    <q-breadcrumbs>
      <template v-for="(element, index) in elements" :key="index">
        <q-breadcrumbs-el
          v-if="!element.options || element.options.length === 0"
          class="breadcrumbs__element"
          :label="getRouteTranslation(element)"
          :to="element.to"
          :icon="element.icon"
        />
        <q-breadcrumbs-el v-else class="breadcrumbs__element breadcrumbs__element--dropdown">
          <q-btn-dropdown
            flat
            dense
            no-caps
            content-class="breadcrumbs__dropdown-content"
            dropdown-icon="o_expand_more"
            class="breadcrumbs__dropdown-btn"
            menu-anchor="bottom left"
            menu-self="top left"
          >
            <template #label>
              <span class="breadcrumbs__dropdown-label">
                <q-icon v-if="element.icon" class="breadcrumbs__dropdown-label-icon" :name="element.icon" />
                <span class="breadcrumbs__dropdown-label-text">
                  {{ getRouteTranslation(element) }}
                </span>
              </span>
            </template>
            <q-list dense class="breadcrumbs__dropdown-list">
              <q-item
                v-for="option in element.options"
                :key="option.id"
                v-close-popup
                clickable
                :to="option.to"
                :disable="!option.to"
                :active="option.id === element.activeId"
                class="breadcrumbs__dropdown-item"
                active-class="breadcrumbs__dropdown-item--active"
              >
                <q-item-section class="breadcrumbs__dropdown-option">
                  <q-icon v-if="option.icon" :name="option.icon" class="breadcrumbs__dropdown-option-icon" />
                  <span>{{ resolveOptionLabel(option) }}</span>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </q-breadcrumbs-el>
      </template>
    </q-breadcrumbs>
  </div>
</template>

<style lang="scss">
.breadcrumbs {
  &__element {
    display: block !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 350px;

    &--dropdown {
      display: flex !important;
    }
  }

  &__dropdown-label-text {
    font-size: 16px;
  }

  &__dropdown-label {
    display: inline-flex;
    align-items: center;
  }

  &__dropdown-label-icon {
    font-size: 125% !important;
    margin-right: 8px;
    vertical-align: middle;
  }

  &__dropdown-option {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 8px;
  }

  &__dropdown-option-icon {
    font-size: 20px;
  }

  &__dropdown-item {
    justify-content: flex-start !important;

    .breadcrumbs__dropdown-option {
      &.column {
        flex-wrap: inherit;
        align-items: start !important;

        .q-icon {
          margin-top: 5px;
        }
      }
    }
  }

  &__dropdown-item .q-item__section {
    justify-content: flex-start !important;
    text-align: left;
  }

  &__dropdown-item--active {
    box-shadow: inset 3px 0 0 var(--q-primary);
    background-color: rgba(0, 0, 0, 0.04);
  }

  &__dropdown-item--active .breadcrumbs__dropdown-option-icon {
    color: var(--q-primary);
  }

  &__dropdown-item--active .breadcrumbs__dropdown-option span {
    color: var(--q-primary);
  }

  .q-btn-dropdown__arrow {
    margin-left: 2px !important;
  }

  .q-breadcrumbs {
    .q-gutter-sm {
      margin-left: 0;
    }

    .q-breadcrumbs__separator {
      margin-left: 8px;
    }

    .q-breadcrumbs__separator ~ .q-breadcrumbs__separator {
      margin-left: 0;
    }
  }

  @media (max-width: 1500px) {
    .q-breadcrumbs > .q-gutter-sm > .items-center {
      margin-left: 4px;
    }

    .q-breadcrumbs__separator ~ .q-breadcrumbs__separator {
      margin-left: 0 !important;
    }

    .q-breadcrumbs > .q-gutter-sm > .q-breadcrumbs__separator {
      margin-top: 0;
      margin-left: 4px;
    }

    .q-breadcrumbs > .q-gutter-sm > .items-center > .q-breadcrumbs__el > .q-btn {
      padding: 0;
    }
  }
}
</style>
